import { useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useAccessiblePages } from '../../hooks/useAccessiblePages';
import { mainNavGroups, settingsNavGroup, type NavGroup, type NavItem } from './navigationData';
import { useSidebarCollapsed } from '../../hooks/useSidebarCollapsed';
import { useCustomerDuplicateAttention } from '../../hooks/useCustomerDuplicateAttention';

const CUSTDUP_PAGE_KEY = 'customer-duplicates';
const CUSTDUP_PAGE_URL = '/customer-duplicates';

/**
 * A badge is a count PLUS what it means — a number without its noun is a number people learn
 * to ignore.
 */
interface NavBadge {
    count: number;
    /** Full tooltip, e.g. "3 new duplicate candidates since the last run". */
    title: string;
    /** What the collapsed sidebar appends after the page name. */
    shortTitle: string;
}

export function Sidebar() {
    const { user } = useAuth();
    const { pages, isLoading } = useAccessiblePages();
    const [collapsed, setCollapsed] = useSidebarCollapsed();
    const [manuallyToggled, setManuallyToggled] = useState<Record<string, boolean>>({});
    const location = useLocation();

    const accessiblePageUrls = new Set(pages.map(p => p.pageUrl));

    // "N new duplicate candidates" from the nightly run (P5) — only when the page list has loaded
    // and the user can see the page, so a badge never tells somebody the size of a queue they may
    // not open.
    const custDupAccessible = !isLoading && accessiblePageUrls.has(CUSTDUP_PAGE_URL);
    const { count: custDupNew } = useCustomerDuplicateAttention(custDupAccessible);

    const badges: Record<string, NavBadge> = {
        [CUSTDUP_PAGE_KEY]: {
            count: custDupNew,
            title: `${custDupNew} new duplicate candidate${custDupNew === 1 ? '' : 's'} since the last run`,
            shortTitle: `${custDupNew} new`,
        },
    };

    function isGroupActive(group: NavGroup): boolean {
        return group.items.some(item => location.pathname.startsWith(item.path));
    }

    function isGroupExpanded(group: NavGroup): boolean {
        if (manuallyToggled[group.label] !== undefined) {
            return manuallyToggled[group.label];
        }
        return isGroupActive(group);
    }

    function toggleGroup(group: NavGroup) {
        setManuallyToggled(prev => ({
            ...prev,
            [group.label]: !isGroupExpanded(group),
        }));
    }

    /**
     * Check if a nav item should be visible.
     * For items under collapsible groups (Settings), we only check adminOnly
     * since they share the parent page permission (/settings).
     * For regular items, we also check page-level permissions.
     */
    function isItemVisible(item: NavItem, skipPageCheck: boolean): boolean {
        if (item.adminOnly && !user?.isAdmin) return false;
        if (skipPageCheck) return true;
        // During loading, don't filter by pages yet (prevent flash)
        if (isLoading) return true;
        return accessiblePageUrls.has(item.path) ||
            pages.some(p => item.path.startsWith(p.pageUrl + '/'));
    }

    function renderNavItem(item: NavItem) {
        const badge = badges[item.pageKey];
        const badgeCount = badge?.count ?? 0;
        const showBadge = badgeCount > 0;
        return (
            <NavLink
                key={item.path}
                to={item.path}
                title={collapsed ? `${item.label}${showBadge ? ` — ${badge?.shortTitle}` : ''}` : undefined}
                className={({ isActive }) =>
                    `flex items-center ${
                        collapsed ? 'justify-center' : 'gap-3 pl-8'
                    } px-3 py-2 rounded-lg text-sm transition-colors relative ${
                        isActive
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                    }`
                }
            >
                <span className="relative flex-shrink-0">
                    <item.icon className="w-4 h-4" />
                    {showBadge && collapsed && (
                        <span className="absolute -right-1.5 -top-1.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-gray-900" />
                    )}
                </span>
                {!collapsed && (
                    <>
                        <span className="whitespace-nowrap overflow-hidden">{item.label}</span>
                        {showBadge && (
                            <span
                                className="ml-auto min-w-[1.25rem] rounded-full bg-amber-500 px-1.5 py-0.5 text-center text-[10px] font-semibold text-white"
                                title={badge?.title}
                            >
                                {badgeCount}
                            </span>
                        )}
                    </>
                )}
            </NavLink>
        );
    }

    function renderGroup(group: NavGroup) {
        const skipPageCheck = group.collapsible;
        const visibleItems = group.items.filter(item => isItemVisible(item, skipPageCheck));

        // Non-collapsible: hide entire group if no visible items
        if (!group.collapsible && visibleItems.length === 0 && group.items.length > 0) return null;
        // Collapsible: hide if no items pass visibility (e.g. non-admin user)
        if (group.collapsible && visibleItems.length === 0) return null;

        const isExpanded = isGroupExpanded(group);
        const isActive = isGroupActive(group);
        const showItems = group.collapsible ? isExpanded : true;

        return (
            <div key={group.label} className="space-y-1">
                {group.collapsible ? (
                    <button
                        onClick={() => toggleGroup(group)}
                        title={collapsed ? group.label : undefined}
                        className={`w-full flex items-center ${
                            collapsed ? 'justify-center' : 'gap-3'
                        } px-3 py-2 rounded-lg text-sm transition-colors ${
                            isActive
                                ? 'text-blue-400'
                                : 'text-gray-400 hover:bg-gray-800 hover:text-white'
                        }`}
                    >
                        <group.icon className="w-5 h-5 flex-shrink-0" />
                        {!collapsed && (
                            <>
                                <span className="whitespace-nowrap overflow-hidden font-medium">
                                    {group.label}
                                </span>
                                <ChevronDown
                                    className={`w-4 h-4 ml-auto transition-transform ${
                                        isExpanded ? 'rotate-180' : ''
                                    }`}
                                />
                            </>
                        )}
                    </button>
                ) : (
                    <div
                        className={`flex items-center ${
                            collapsed ? 'justify-center' : 'gap-3'
                        } px-3 py-2 text-xs font-semibold uppercase tracking-wider ${
                            collapsed ? 'text-gray-600' : 'text-gray-500'
                        }`}
                        title={collapsed ? group.label : undefined}
                    >
                        {collapsed ? (
                            <group.icon className="w-4 h-4" />
                        ) : (
                            <span>{group.label}</span>
                        )}
                    </div>
                )}

                {showItems && visibleItems.map(renderNavItem)}
            </div>
        );
    }

    return (
        <aside
            className={`${
                collapsed ? 'w-16' : 'w-64'
            } bg-gray-900 text-white h-screen sticky top-0 flex flex-col overflow-y-auto transition-all duration-300 ease-in-out pt-safe-top`}
        >
            {/* Header */}
            <div className="p-4 border-b border-gray-700 flex items-center justify-between min-h-[57px]">
                {!collapsed && (
                    <div className="overflow-hidden">
                        <h1 className="text-lg font-bold whitespace-nowrap">Reporting App</h1>
                        <p className="text-xs text-gray-400 mt-1">Dolder Group</p>
                    </div>
                )}
                <button
                    onClick={() => setCollapsed(!collapsed)}
                    className="p-1.5 rounded-lg text-gray-400 hover:bg-gray-800 hover:text-white transition-colors flex-shrink-0"
                    aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                    title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    {collapsed ? (
                        <ChevronRight className="w-5 h-5" />
                    ) : (
                        <ChevronLeft className="w-5 h-5" />
                    )}
                </button>
            </div>

            {/* Main navigation groups */}
            <nav className="flex-1 p-2 space-y-4">
                {mainNavGroups.map(renderGroup)}
            </nav>

            {/* Settings group at bottom */}
            {user?.isAdmin && (
                <div className="p-2 border-t border-gray-700">
                    {renderGroup(settingsNavGroup)}
                </div>
            )}

            {/* User profile */}
            {user && (
                <div className="p-4 border-t border-gray-700">
                    {collapsed ? (
                        <div
                            className="w-8 h-8 rounded-full bg-gray-700 flex items-center justify-center text-xs font-medium mx-auto"
                            title={user.email}
                        >
                            {user.email?.charAt(0).toUpperCase()}
                        </div>
                    ) : (
                        <>
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                            <p className="text-xs text-gray-500">{user.roles.join(', ')}</p>
                        </>
                    )}
                </div>
            )}
        </aside>
    );
}
